import dayjs from 'dayjs'

/*
 * Google only returns a refresh_token on the *first* consent for a given
 * (user, client) pair, so tokens.refresh_token is routinely undefined on a
 * repeat sign-in. Writing that straight into localStorage stores the literal
 * string "undefined", which reads back as truthy and then gets sent to
 * /token/refresh forever. Treat those sentinel values as absent so a browser
 * already holding one heals itself on the next read.
 */
const readStoredToken = (key) => {
  let value = localStorage.getItem(key);

  if (!value || value === "undefined" || value === "null") {
    return null;
  }

  return value;
};

export const fetchAccessTokenService = async (code) => {
  try {
    let response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/oauth/token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          code
        })
      }
    );

    if (!response.ok) {
      let body = await response.json();
      throw new Error(body.message);
    }

    let tokens = await response.json();

    localStorage.setItem("googleAccessToken", tokens.access_token);
    localStorage.setItem("googleAccessTokenExpiryDate", tokens.expiry_date);

    if (tokens.refresh_token) {
      localStorage.setItem("googleRefreshToken", tokens.refresh_token);
    } else {
      localStorage.removeItem("googleRefreshToken");
    }

    /*
     * The account that just signed in is not necessarily the one from the
     * previous session. A stored profile id can belong to a CM360 profile this
     * account no longer has, so drop it and let the user pick again —
     * /api/dcm/profiles only needs the access token.
     */
    localStorage.removeItem("googleProfileId");
  } catch (e) {
    console.log(e);
    throw e;
  }
};

const refreshGoogleAccessToken = async (refreshToken) => {
  try {
    let response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/oauth/token/refresh`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          refreshToken
        })
      }
    );

    if (!response.ok) {
      let body = await response.json();
      throw new Error(body.message);
    }

    let tokens = await response.json();

    let expiry_date = dayjs().add(45, "minutes").valueOf();
    localStorage.setItem("googleAccessToken", tokens.access_token);
    localStorage.setItem("googleAccessTokenExpiryDate", expiry_date);
    return tokens.access_token;
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const makeAuthHeaders = async () => {
  let accessToken = readStoredToken("googleAccessToken");
  let refreshToken = readStoredToken("googleRefreshToken");

  if (!accessToken) {
    return {};
  }

  let tokenExpiryTimestamp = parseInt(
    localStorage.getItem("googleAccessTokenExpiryDate"),
    10
  );

  /*
   * Without a refresh token the session just ends when the access token does:
   * the 401 trips AuthErrorHandler and sends the user back to /login. That is
   * far better than the previous behaviour, where having no refresh token meant
   * sending no auth header at all and every call failed immediately.
   */
  if (refreshToken && Date.now() >= tokenExpiryTimestamp) {
    await refreshGoogleAccessToken(refreshToken);
  }

  let headers = {
    "X-GoogleAccessToken": localStorage.getItem("googleAccessToken")
  };

  let profileId = localStorage.getItem("googleProfileId");

  if (profileId) {
    headers["X-GoogleProfileId"] = profileId;
  }

  return headers;
};
